import { Controller } from "@hotwired/stimulus"
import { CANVAS_SIZES, INK_FREE_ZONES, BG_SIZE_OPTIONS, getCanvasDimensions, renderBackground, applyCanvasSize } from "./card_builder/canvas_renderer"
import { FONTS, getFontFamily } from "./card_builder/fonts"
import { STARTER_TEMPLATES, getTemplate } from "./card_builder/templates"
import { renderTextElement, renderEventDetailsElement, renderImageElement, renderShapeElement, renderQrPlaceholder, getElementTypeName, getElementPreview, escapeHtml } from "./card_builder/element_renderer"
import { HistoryManager } from "./card_builder/history_manager"

export default class extends Controller {
  static targets = [
    "frontCanvas", "backCanvas", "frontCanvasWrapper", "backCanvasWrapper",
    "frontGuides", "backGuides", "inkFreeZone",
    "propertiesPanel", "textProperties", "eventDetailsProperties", "shapeProperties", "imageProperties",
    "bgType", "bgColor1", "bgColor1Text", "bgColor2", "bgColor2Text", "gradientOptions", "bgImageUpload", "bgImageOptions", "bgImageSize", "bgImagePosition",
    "orientationLandscape", "orientationPortrait", "orientationInput",
    "textContent", "fontFamily", "fontSize", "fontColor", "textAlign", "fontBold", "fontItalic",
    "showTitle", "showDate", "showLocation", "showHost", "detailsColor",
    "shapeType", "shapeFill", "shapeBorder", "shapeOpacity",
    "posX", "posY", "elWidth", "elHeight",
    "showGuides", "includeQr", "guestCheckbox",
    "form", "designData", "sizeInput",
    "previewModal", "previewFront", "previewBack",
    "imageInput", "bgImageInput",
    "layersPanel", "layersList",
    "templateModal", "templateGrid"
  ]

  static values = {
    design: Object,
    size: String,
    orientation: { type: String, default: "landscape" },
    eventId: Number,
    cardType: String,
    eventTitle: String,
    eventDate: String,
    eventTime: String,
    eventLocation: String,
    hostName: String
  }

  connect() {
    this.currentSide = "front"
    this.selectedElement = null
    this.elementIdCounter = 100
    this.historyManager = new HistoryManager()
    this.isDragging = false
    this.isResizing = false
    this.dragOffset = { x: 0, y: 0 }
    this.hasUnsavedChanges = false

    this.design = JSON.parse(JSON.stringify(this.designValue))
    this.initialDesign = JSON.stringify(this.designValue)
    this.orientation = this.design.orientation || this.orientationValue || "landscape"

    this.setupCanvasEvents()
    this.setupKeyboardShortcuts()
    this.setupUnsavedChangesWarning()
    this.applyCanvasSize()
    this.renderCanvas("front")
    this.renderCanvas("back")
    this.updateBackgroundControls()
    this.renderLayers()

    if (this.design.front.elements.length === 0 && this.design.back.elements.length === 0) {
      setTimeout(() => this.showTemplates(), 100)
    }
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleKeyDown)
    window.removeEventListener("beforeunload", this.boundBeforeUnload)
  }

  setupUnsavedChangesWarning() {
    this.boundBeforeUnload = (e) => {
      if (this.hasUnsavedChanges) {
        e.preventDefault()
        e.returnValue = ""
        return ""
      }
    }
    window.addEventListener("beforeunload", this.boundBeforeUnload)
  }

  markAsChanged() {
    this.hasUnsavedChanges = true
  }

  markAsSaved() {
    this.hasUnsavedChanges = false
  }

  get eventData() {
    return {
      title: this.eventTitleValue || "Event Title",
      date: this.eventDateValue || "January 1, 2025",
      time: this.eventTimeValue || "6:00 PM",
      location: this.eventLocationValue || "Event Location",
      hostName: this.hostNameValue || "Host Name"
    }
  }

  setupCanvasEvents() {
    [this.frontCanvasTarget, this.backCanvasTarget].forEach(canvas => {
      canvas.addEventListener("dragover", (e) => this.handleDragOver(e))
      canvas.addEventListener("drop", (e) => this.handleDrop(e))
      canvas.addEventListener("mousemove", (e) => this.handleMouseMove(e))
      canvas.addEventListener("mouseup", (e) => this.handleMouseUp(e))
      canvas.addEventListener("mouseleave", (e) => this.handleMouseUp(e))
    })
  }

  setupKeyboardShortcuts() {
    this.handleKeyDown = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.tagName === "SELECT") {
        return
      }

      const el = this.selectedElement ? this.findElement(this.selectedElement) : null

      switch (e.key) {
        case "Delete":
        case "Backspace":
          if (this.selectedElement) {
            e.preventDefault()
            this.deleteElement()
          }
          break
        case "ArrowUp":
          if (el) { e.preventDefault(); this.nudgeElement(0, e.shiftKey ? -10 : -1) }
          break
        case "ArrowDown":
          if (el) { e.preventDefault(); this.nudgeElement(0, e.shiftKey ? 10 : 1) }
          break
        case "ArrowLeft":
          if (el) { e.preventDefault(); this.nudgeElement(e.shiftKey ? -10 : -1, 0) }
          break
        case "ArrowRight":
          if (el) { e.preventDefault(); this.nudgeElement(e.shiftKey ? 10 : 1, 0) }
          break
        case "d":
          if ((e.ctrlKey || e.metaKey) && this.selectedElement) {
            e.preventDefault()
            this.duplicateElement()
          }
          break
        case "z":
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault()
            e.shiftKey ? this.redo() : this.undo()
          }
          break
        case "Escape":
          this.hideProperties()
          this.selectedElement = null
          this.renderCanvas(this.currentSide)
          break
      }
    }
    document.addEventListener("keydown", this.handleKeyDown)
  }

  nudgeElement(dx, dy) {
    const el = this.findElement(this.selectedElement)
    if (!el) return

    this.saveState()
    el.x = (el.x || 0) + dx
    el.y = (el.y || 0) + dy
    this.renderCanvas(this.currentSide)
    this.showProperties(el)
  }

  renderCanvas(side) {
    const canvas = side === "front" ? this.frontCanvasTarget : this.backCanvasTarget
    const sideData = this.design[side]

    renderBackground(canvas, sideData.background)
    canvas.querySelectorAll(".builder-element").forEach(el => el.remove())

    ;(sideData.elements || []).forEach((el, index) => {
      const domEl = this.createElementDOM(el, index)
      canvas.appendChild(domEl)
    })
  }

  createElementDOM(el, zIndex = 0) {
    const div = document.createElement("div")
    div.className = "builder-element absolute cursor-move select-none"
    div.dataset.elementId = el.id
    div.style.left = `${el.x}px`
    div.style.top = `${el.y}px`
    div.style.width = `${el.width || 200}px`
    div.style.zIndex = zIndex + 1

    if (this.selectedElement === el.id) {
      div.classList.add("ring-2", "ring-blue-500", "ring-offset-1")
    }

    switch (el.type) {
      case "text":
        div.innerHTML = renderTextElement(el)
        break
      case "event_details":
        div.innerHTML = renderEventDetailsElement(el, this.eventData)
        break
      case "image":
        div.innerHTML = renderImageElement(el)
        div.style.height = `${el.height || 100}px`
        break
      case "shape":
        div.innerHTML = renderShapeElement(el)
        div.style.height = `${el.height || 50}px`
        break
      case "qr_placeholder":
        div.innerHTML = renderQrPlaceholder(el)
        div.style.width = `${el.size || 72}px`
        div.style.height = `${el.size || 72}px`
        break
    }

    if (this.selectedElement === el.id) {
      div.appendChild(this.createResizeHandles(el))
    }

    div.addEventListener("mousedown", (e) => this.handleElementMouseDown(e, el))
    div.addEventListener("click", (e) => {
      e.stopPropagation()
      this.selectElement(el.id)
    })

    return div
  }

  createResizeHandles(el) {
    const container = document.createElement("div")
    container.className = "resize-handles"

    const positions = ["nw", "ne", "sw", "se"]
    positions.forEach(pos => {
      const handle = document.createElement("div")
      handle.className = `resize-handle resize-${pos} absolute w-3 h-3 bg-blue-500 border border-white rounded-sm cursor-${pos}-resize`

      switch (pos) {
        case "nw": handle.style.cssText = "top: -5px; left: -5px;"; break
        case "ne": handle.style.cssText = "top: -5px; right: -5px;"; break
        case "sw": handle.style.cssText = "bottom: -5px; left: -5px;"; break
        case "se": handle.style.cssText = "bottom: -5px; right: -5px;"; break
      }

      handle.addEventListener("mousedown", (e) => {
        e.stopPropagation()
        this.startResize(e, el, pos)
      })

      container.appendChild(handle)
    })

    return container
  }

  startResize(e, el, direction) {
    e.preventDefault()
    this.isResizing = true
    this.resizeDirection = direction
    this.resizeStartX = e.clientX
    this.resizeStartY = e.clientY
    this.resizeStartWidth = el.width || 200
    this.resizeStartHeight = el.height || 50
    this.resizeStartLeft = el.x || 0
    this.resizeStartTop = el.y || 0
    this.resizingElement = el
    this.saveState()
  }

  handleMouseMove(e) {
    if (this.isResizing && this.resizingElement) {
      const dx = e.clientX - this.resizeStartX
      const dy = e.clientY - this.resizeStartY
      const el = this.resizingElement
      const maintainRatio = el.type === "image"
      const aspectRatio = this.resizeStartWidth / this.resizeStartHeight

      switch (this.resizeDirection) {
        case "se":
          el.width = Math.max(20, this.resizeStartWidth + dx)
          el.height = maintainRatio ? el.width / aspectRatio : Math.max(20, this.resizeStartHeight + dy)
          break
        case "sw":
          const newWidthSw = Math.max(20, this.resizeStartWidth - dx)
          el.x = this.resizeStartLeft + (this.resizeStartWidth - newWidthSw)
          el.width = newWidthSw
          el.height = maintainRatio ? el.width / aspectRatio : Math.max(20, this.resizeStartHeight + dy)
          break
        case "ne":
          el.width = Math.max(20, this.resizeStartWidth + dx)
          const newHeightNe = Math.max(20, this.resizeStartHeight - dy)
          el.y = this.resizeStartTop + (this.resizeStartHeight - newHeightNe)
          el.height = maintainRatio ? el.width / aspectRatio : newHeightNe
          break
        case "nw":
          const newWidthNw = Math.max(20, this.resizeStartWidth - dx)
          const newHeightNw = Math.max(20, this.resizeStartHeight - dy)
          el.x = this.resizeStartLeft + (this.resizeStartWidth - newWidthNw)
          el.y = this.resizeStartTop + (this.resizeStartHeight - newHeightNw)
          el.width = newWidthNw
          el.height = maintainRatio ? el.width / aspectRatio : newHeightNw
          break
      }

      this.renderCanvas(this.currentSide)
      this.showProperties(el)
    } else if (this.isDragging && this.draggingElement) {
      const canvas = this.currentSide === "front" ? this.frontCanvasTarget : this.backCanvasTarget
      const rect = canvas.getBoundingClientRect()
      const x = e.clientX - rect.left - this.dragOffset.x
      const y = e.clientY - rect.top - this.dragOffset.y

      this.draggingElement.x = Math.max(0, Math.min(x, canvas.offsetWidth - (this.draggingElement.width || 100)))
      this.draggingElement.y = Math.max(0, Math.min(y, canvas.offsetHeight - (this.draggingElement.height || 50)))

      this.renderCanvas(this.currentSide)
    }
  }

  handleMouseUp(e) {
    if (this.isResizing) {
      this.isResizing = false
      this.resizingElement = null
      this.renderLayers()
    }
    if (this.isDragging) {
      this.isDragging = false
      this.draggingElement = null
      this.renderLayers()
    }
  }

  handleElementMouseDown(e, el) {
    if (e.target.classList.contains("resize-handle")) return

    e.preventDefault()
    this.selectElement(el.id)
    this.saveState()

    this.isDragging = true
    this.draggingElement = el

    const elemRect = e.currentTarget.getBoundingClientRect()
    this.dragOffset = {
      x: e.clientX - elemRect.left,
      y: e.clientY - elemRect.top
    }
  }

  handleDragOver(e) { e.preventDefault() }
  handleDrop(e) { e.preventDefault() }

  selectElement(elementId) {
    this.selectedElement = elementId
    this.renderCanvas(this.currentSide)
    this.renderLayers()

    const el = this.findElement(elementId)
    if (!el) {
      this.hideProperties()
      return
    }

    this.showProperties(el)
    this.focusCurrentCanvas()
  }

  focusCurrentCanvas() {
    const canvas = this.currentSide === "front" ? this.frontCanvasTarget : this.backCanvasTarget
    canvas.focus()
  }

  findElement(elementId) {
    for (const side of ["front", "back"]) {
      const el = this.design[side].elements?.find(e => e.id === elementId)
      if (el) {
        this.currentSide = side
        return el
      }
    }
    return null
  }

  showProperties(el) {
    this.propertiesPanelTarget.classList.remove("hidden")

    this.textPropertiesTarget.classList.add("hidden")
    this.eventDetailsPropertiesTarget.classList.add("hidden")
    this.shapePropertiesTarget.classList.add("hidden")
    this.imagePropertiesTarget.classList.add("hidden")

    this.posXTarget.value = Math.round(el.x || 0)
    this.posYTarget.value = Math.round(el.y || 0)
    this.elWidthTarget.value = Math.round(el.width || 200)
    this.elHeightTarget.value = Math.round(el.height || 50)

    switch (el.type) {
      case "text":
        this.textPropertiesTarget.classList.remove("hidden")
        this.textContentTarget.value = el.content || ""
        this.fontFamilyTarget.value = el.font || "Arial"
        this.fontSizeTarget.value = el.fontSize || 14
        this.fontColorTarget.value = el.color || "#333333"
        this.textAlignTarget.value = el.align || "left"
        this.fontBoldTarget.checked = el.fontWeight === "bold"
        this.fontItalicTarget.checked = el.fontStyle === "italic"
        break

      case "event_details":
        this.eventDetailsPropertiesTarget.classList.remove("hidden")
        this.showTitleTarget.checked = el.showTitle || false
        this.showDateTarget.checked = el.showDate || false
        this.showLocationTarget.checked = el.showLocation || false
        this.showHostTarget.checked = el.showHost || false
        this.detailsColorTarget.value = el.color || "#333333"
        break

      case "shape":
        this.shapePropertiesTarget.classList.remove("hidden")
        this.shapeTypeTarget.value = el.shape || "rectangle"
        this.shapeFillTarget.value = el.fill || "#cccccc"
        this.shapeBorderTarget.value = el.borderColor || "#000000"
        this.shapeOpacityTarget.value = el.opacity !== undefined ? el.opacity : 1
        break

      case "image":
        this.imagePropertiesTarget.classList.remove("hidden")
        break
    }
  }

  hideProperties() {
    this.propertiesPanelTarget.classList.add("hidden")
    this.selectedElement = null
  }

  addText() {
    this.saveState()
    const id = `el_${++this.elementIdCounter}`
    const el = {
      id, type: "text", content: "Your text here",
      x: 50, y: 50, width: 200,
      font: "Arial", fontSize: 14, color: "#333333", align: "left"
    }
    this.design[this.currentSide].elements.push(el)
    this.renderCanvas(this.currentSide)
    this.renderLayers()
    this.selectElement(id)
  }

  addEventDetails() {
    this.saveState()
    const id = `el_${++this.elementIdCounter}`
    const el = {
      id, type: "event_details",
      x: 50, y: 50, width: 250,
      showTitle: true, showDate: true, showLocation: true, showHost: false,
      color: "#333333"
    }
    this.design[this.currentSide].elements.push(el)
    this.renderCanvas(this.currentSide)
    this.renderLayers()
    this.selectElement(id)
  }

  addImage() {
    this.imageInputTarget.click()
  }

  handleImageUpload(e) {
    const file = e.target.files[0]
    if (!file) return

    if (file.size > 5 * 1024 * 1024) {
      this.showNotification("Image must be under 5MB", "error")
      return
    }

    const reader = new FileReader()
    reader.onload = (evt) => {
      this.saveState()
      const id = `el_${++this.elementIdCounter}`

      const img = new Image()
      img.onload = () => {
        const maxWidth = 200
        const ratio = img.height / img.width
        const el = {
          id, type: "image",
          x: 50, y: 50,
          width: maxWidth, height: Math.round(maxWidth * ratio),
          url: evt.target.result
        }
        this.design[this.currentSide].elements.push(el)
        this.renderCanvas(this.currentSide)
        this.renderLayers()
        this.selectElement(id)
      }
      img.src = evt.target.result
    }
    reader.readAsDataURL(file)
    e.target.value = ""
  }

  addShape() {
    this.saveState()
    const id = `el_${++this.elementIdCounter}`
    const el = {
      id, type: "shape", shape: "rectangle",
      x: 50, y: 50, width: 100, height: 50,
      fill: "#e5e7eb", borderColor: "transparent", borderWidth: 0, opacity: 1
    }
    this.design[this.currentSide].elements.push(el)
    this.renderCanvas(this.currentSide)
    this.renderLayers()
    this.selectElement(id)
  }

  duplicateElement() {
    if (!this.selectedElement) return

    const el = this.findElement(this.selectedElement)
    if (!el) return

    this.saveState()
    const newEl = JSON.parse(JSON.stringify(el))
    newEl.id = `el_${++this.elementIdCounter}`
    newEl.x = (newEl.x || 0) + 20
    newEl.y = (newEl.y || 0) + 20

    this.design[this.currentSide].elements.push(newEl)
    this.renderCanvas(this.currentSide)
    this.renderLayers()
    this.selectElement(newEl.id)
  }

  updateElement() {
    if (!this.selectedElement) return

    const el = this.findElement(this.selectedElement)
    if (!el) return

    this.saveState()

    el.x = parseInt(this.posXTarget.value) || 0
    el.y = parseInt(this.posYTarget.value) || 0
    el.width = parseInt(this.elWidthTarget.value) || 200
    el.height = parseInt(this.elHeightTarget.value) || 50

    switch (el.type) {
      case "text":
        el.content = this.textContentTarget.value
        el.font = this.fontFamilyTarget.value
        el.fontSize = parseInt(this.fontSizeTarget.value) || 14
        el.color = this.fontColorTarget.value
        el.align = this.textAlignTarget.value
        el.fontWeight = this.fontBoldTarget.checked ? "bold" : null
        el.fontStyle = this.fontItalicTarget.checked ? "italic" : null
        break

      case "event_details":
        el.showTitle = this.showTitleTarget.checked
        el.showDate = this.showDateTarget.checked
        el.showLocation = this.showLocationTarget.checked
        el.showHost = this.showHostTarget.checked
        el.color = this.detailsColorTarget.value
        break

      case "shape":
        el.shape = this.shapeTypeTarget.value
        el.fill = this.shapeFillTarget.value
        el.borderColor = this.shapeBorderTarget.value
        el.opacity = parseFloat(this.shapeOpacityTarget.value)
        break
    }

    this.renderCanvas(this.currentSide)
    this.renderLayers()
  }

  deleteElement() {
    if (!this.selectedElement) return

    this.saveState()
    for (const side of ["front", "back"]) {
      this.design[side].elements = this.design[side].elements.filter(
        e => e.id !== this.selectedElement
      )
    }
    this.hideProperties()
    this.renderCanvas("front")
    this.renderCanvas("back")
    this.renderLayers()
  }

  bringForward() { if (this.selectedElement) this.moveElementInStack(1) }
  sendBackward() { if (this.selectedElement) this.moveElementInStack(-1) }

  bringToFront() {
    if (!this.selectedElement) return
    const elements = this.design[this.currentSide].elements
    const index = elements.findIndex(e => e.id === this.selectedElement)
    if (index === -1 || index === elements.length - 1) return

    this.saveState()
    const [el] = elements.splice(index, 1)
    elements.push(el)
    this.renderCanvas(this.currentSide)
    this.renderLayers()
  }

  sendToBack() {
    if (!this.selectedElement) return
    const elements = this.design[this.currentSide].elements
    const index = elements.findIndex(e => e.id === this.selectedElement)
    if (index <= 0) return

    this.saveState()
    const [el] = elements.splice(index, 1)
    elements.unshift(el)
    this.renderCanvas(this.currentSide)
    this.renderLayers()
  }

  moveElementInStack(direction) {
    const elements = this.design[this.currentSide].elements
    const index = elements.findIndex(e => e.id === this.selectedElement)
    const newIndex = index + direction

    if (newIndex < 0 || newIndex >= elements.length) return

    this.saveState()
    const [el] = elements.splice(index, 1)
    elements.splice(newIndex, 0, el)
    this.renderCanvas(this.currentSide)
    this.renderLayers()
  }

  renderLayers() {
    if (!this.hasLayersListTarget) return

    const elements = this.design[this.currentSide].elements || []
    const html = elements.slice().reverse().map((el, reverseIndex) => {
      const index = elements.length - 1 - reverseIndex
      const isSelected = el.id === this.selectedElement
      const typeName = getElementTypeName(el)
      const preview = getElementPreview(el)

      return `
        <div class="layer-item flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-100 ${isSelected ? 'bg-blue-50 ring-1 ring-blue-300' : ''}"
             data-action="click->card-builder#selectLayerItem"
             data-element-id="${el.id}">
          <span class="text-gray-400 text-xs w-4">${index + 1}</span>
          <span class="text-sm flex-1 truncate">${typeName}: ${preview}</span>
        </div>
      `
    }).join('')

    this.layersListTarget.innerHTML = html || '<p class="text-xs text-gray-400 p-2">No elements</p>'
  }

  selectLayerItem(e) {
    const id = e.currentTarget.dataset.elementId
    this.selectElement(id)
  }

  updateBackground() {
    this.saveState()
    const sideData = this.design[this.currentSide]

    const bgType = this.bgTypeTarget.value
    if (bgType === "image") {
      sideData.background = {
        type: "image",
        url: sideData.background?.url || "",
        size: this.hasBgImageSizeTarget ? this.bgImageSizeTarget.value : (sideData.background?.size || "cover"),
        position: this.hasBgImagePositionTarget ? this.bgImagePositionTarget.value : (sideData.background?.position || "center"),
        color: this.bgColor1Target.value
      }
    } else if (bgType === "gradient") {
      sideData.background = {
        type: "gradient",
        color1: this.bgColor1Target.value,
        color2: this.bgColor2Target.value,
        angle: 135
      }
    } else {
      sideData.background = { type: "solid", color: this.bgColor1Target.value }
    }

    this.bgColor1TextTarget.value = this.bgColor1Target.value
    this.bgColor2TextTarget.value = this.bgColor2Target.value

    this.toggleGradientOptions()
    this.renderCanvas(this.currentSide)
  }

  uploadBackgroundImage() {
    if (this.hasBgImageInputTarget) {
      this.bgImageInputTarget.click()
    }
  }

  handleBgImageUpload(e) {
    const file = e.target.files[0]
    if (!file) return

    if (file.size > 5 * 1024 * 1024) {
      this.showNotification("Image must be under 5MB", "error")
      return
    }

    const reader = new FileReader()
    reader.onload = (evt) => {
      this.saveState()
      this.design[this.currentSide].background = {
        type: "image", url: evt.target.result, size: "cover", position: "center"
      }
      this.bgTypeTarget.value = "image"
      this.toggleGradientOptions()
      this.updateBackgroundControls()
      this.renderCanvas(this.currentSide)
    }
    reader.readAsDataURL(file)
    e.target.value = ""
  }

  updateBackgroundControls() {
    const bg = this.design[this.currentSide].background
    this.bgTypeTarget.value = bg.type || "solid"
    this.bgColor1Target.value = bg.color1 || bg.color || "#ffffff"
    this.bgColor1TextTarget.value = bg.color1 || bg.color || "#ffffff"
    this.bgColor2Target.value = bg.color2 || "#ffffff"
    this.bgColor2TextTarget.value = bg.color2 || "#ffffff"

    if (this.hasBgImageSizeTarget) this.bgImageSizeTarget.value = bg.size || "cover"
    if (this.hasBgImagePositionTarget) this.bgImagePositionTarget.value = bg.position || "center"

    this.toggleGradientOptions()
  }

  toggleGradientOptions() {
    const bgType = this.bgTypeTarget.value
    this.gradientOptionsTarget.classList.toggle("hidden", bgType !== "gradient")
    if (this.hasBgImageOptionsTarget) {
      this.bgImageOptionsTarget.classList.toggle("hidden", bgType !== "image")
    }
  }

  applyTheme(e) {
    const theme = JSON.parse(e.currentTarget.dataset.theme)
    this.saveState()

    this.design[this.currentSide].background = {
      type: "gradient", color1: theme.primary, color2: theme.accent, angle: 135
    }
    this.updateBackgroundControls()
    this.renderCanvas(this.currentSide)
  }

  showTemplates() {
    if (this.hasTemplateModalTarget) this.templateModalTarget.showModal()
  }

  closeTemplates() {
    if (this.hasTemplateModalTarget) this.templateModalTarget.close()
  }

  selectTemplate(e) {
    const templateId = e.currentTarget.dataset.templateId
    const template = getTemplate(templateId)
    if (!template) return

    this.saveState()

    this.design.front = JSON.parse(JSON.stringify(template.front))
    this.design.back = JSON.parse(JSON.stringify(template.back))

    let counter = this.elementIdCounter
    for (const side of ["front", "back"]) {
      this.design[side].elements.forEach(el => {
        el.id = `el_${++counter}`
      })
    }
    this.elementIdCounter = counter

    this.closeTemplates()
    this.renderCanvas("front")
    this.renderCanvas("back")
    this.renderLayers()
    this.updateBackgroundControls()
  }

  selectSide(e) {
    this.currentSide = e.currentTarget.dataset.side
    this.updateBackgroundControls()
    this.hideProperties()
    this.renderLayers()
  }

  canvasClick(e) {
    const canvas = e.currentTarget
    this.currentSide = canvas.dataset.side
    this.updateBackgroundControls()
    this.renderLayers()
    canvas.focus()

    if (e.target === canvas) {
      this.hideProperties()
      this.selectedElement = null
      this.renderCanvas(this.currentSide)
    }
  }

  changeSize(e) {
    const newSize = e.currentTarget.value
    this.sizeValue = newSize
    this.design.size = newSize
    this.applyCanvasSize()
  }

  changeOrientation(e) {
    const newOrientation = e.currentTarget.value
    this.orientation = newOrientation
    this.design.orientation = newOrientation
    this.applyCanvasSize()
    this.renderCanvas("front")
    this.renderCanvas("back")
  }

  applyCanvasSize() {
    const size = this.sizeValue || "4x6"
    applyCanvasSize(
      this.frontCanvasTarget,
      this.backCanvasTarget,
      this.inkFreeZoneTarget,
      size,
      this.orientation
    )
    if (this.hasSizeInputTarget) this.sizeInputTarget.value = size
  }

  getCanvasDimensions() {
    return getCanvasDimensions(this.sizeValue || "4x6", this.orientation || "landscape")
  }

  toggleGuides() {
    const show = this.showGuidesTarget.checked
    this.frontGuidesTarget.classList.toggle("hidden", !show)
    this.backGuidesTarget.classList.toggle("hidden", !show)
  }

  toggleQr() {
    const include = this.includeQrTarget.checked
    if (include) {
      const existing = this.design.back.elements.find(e => e.type === "qr_placeholder")
      if (!existing) {
        this.design.back.elements.push({
          id: `el_${++this.elementIdCounter}`,
          type: "qr_placeholder", x: 350, y: 10, size: 72
        })
      }
    } else {
      this.design.back.elements = this.design.back.elements.filter(e => e.type !== "qr_placeholder")
    }
    this.renderCanvas("back")
    this.renderLayers()
  }

  selectAllGuests() { this.guestCheckboxTargets.forEach(cb => cb.checked = true) }
  clearGuests() { this.guestCheckboxTargets.forEach(cb => cb.checked = false) }

  saveState() {
    this.historyManager.saveState(this.design)
    this.markAsChanged()
  }

  undo() {
    const previous = this.historyManager.undo(this.design)
    if (previous) {
      this.design = previous
      this.renderCanvas("front")
      this.renderCanvas("back")
      this.renderLayers()
      this.hideProperties()
    }
  }

  redo() {
    const next = this.historyManager.redo(this.design)
    if (next) {
      this.design = next
      this.renderCanvas("front")
      this.renderCanvas("back")
      this.renderLayers()
      this.hideProperties()
    }
  }

  saveDraft() {
    localStorage.setItem(`card_draft_${this.eventIdValue}`, JSON.stringify(this.design))
    this.markAsSaved()
    this.showNotification("Draft saved locally", "success")
  }

  showNotification(message, type = "info") {
    if (window.showToast) {
      window.showToast(message, type)
    } else {
      // Fallback: create inline notification
      const notification = document.createElement("div")
      notification.className = `fixed top-4 right-4 p-4 rounded-lg shadow-lg z-50 ${
        type === "error" ? "bg-red-100 text-red-800" :
        type === "success" ? "bg-green-100 text-green-800" :
        "bg-blue-100 text-blue-800"
      }`
      notification.setAttribute("role", type === "error" ? "alert" : "status")
      notification.textContent = message
      document.body.appendChild(notification)

      setTimeout(() => notification.remove(), 5000)
    }
  }

  openPreview() {
    const frontHtml = this.generatePreviewHtml("front")
    const backHtml = this.generatePreviewHtml("back")
    const dims = this.getCanvasDimensions()

    this.previewFrontTarget.style.width = `${dims.width}px`
    this.previewFrontTarget.style.height = `${dims.height}px`
    this.previewBackTarget.style.width = `${dims.width}px`
    this.previewBackTarget.style.height = `${dims.height}px`

    this.previewFrontTarget.srcdoc = frontHtml
    this.previewBackTarget.srcdoc = backHtml
    this.previewModalTarget.showModal()
  }

  closePreview() { this.previewModalTarget.close() }

  generatePreviewHtml(side) {
    const sideData = this.design[side]
    const dims = this.getCanvasDimensions()
    const bg = sideData.background
    let bgStyle = ""

    if (bg.type === "gradient") {
      bgStyle = `background: linear-gradient(${bg.angle || 135}deg, ${bg.color1} 0%, ${bg.color2} 100%);`
    } else if (bg.type === "image" && bg.url) {
      const size = BG_SIZE_OPTIONS[bg.size] || "cover"
      bgStyle = `background: url('${bg.url}') ${bg.position || "center"}/${size} no-repeat;`
    } else {
      bgStyle = `background: ${bg.color || '#ffffff'};`
    }

    let elementsHtml = ""
    ;(sideData.elements || []).forEach(el => {
      elementsHtml += this.generateElementHtml(el)
    })

    return `<!DOCTYPE html>
<html style="margin:0;padding:0;width:100%;height:100%;">
<head>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    html, body { width: 100%; height: 100%; ${bgStyle} font-family: Arial, sans-serif; position: relative; overflow: hidden; }
    .element { position: absolute; }
  </style>
</head>
<body>${elementsHtml}</body>
</html>`
  }

  generateElementHtml(el) {
    const baseStyle = `left: ${el.x}px; top: ${el.y}px; width: ${el.width || 200}px;`
    const data = this.eventData

    switch (el.type) {
      case "text":
        const fontFamily = getFontFamily(el.font)
        return `<div class="element" style="${baseStyle} font-family: ${fontFamily}; font-size: ${el.fontSize || 14}pt; color: ${el.color || '#333'}; text-align: ${el.align || 'left'}; ${el.fontWeight ? 'font-weight: ' + el.fontWeight + ';' : ''} ${el.fontStyle ? 'font-style: ' + el.fontStyle + ';' : ''}">${escapeHtml(el.content || '')}</div>`
      case "event_details":
        let parts = ""
        if (el.showTitle) parts += `<div style="font-size: 16pt; font-weight: bold; margin-bottom: 8px;">${escapeHtml(data.title)}</div>`
        if (el.showDate) parts += `<div style="font-size: 11pt; margin-bottom: 4px;"><strong>When:</strong> ${escapeHtml(data.date)} at ${escapeHtml(data.time)}</div>`
        if (el.showLocation) parts += `<div style="font-size: 11pt; margin-bottom: 4px;"><strong>Where:</strong> ${escapeHtml(data.location)}</div>`
        if (el.showHost) parts += `<div style="font-size: 11pt; margin-top: 8px;">Hosted by ${escapeHtml(data.hostName)}</div>`
        return `<div class="element" style="${baseStyle} color: ${el.color || '#333'};">${parts}</div>`
      case "image":
        return `<img class="element" src="${el.url}" style="${baseStyle} height: ${el.height || 100}px; object-fit: cover;" />`
      case "shape":
        return `<div class="element" style="${baseStyle} height: ${el.height || 50}px; background: ${el.fill || 'transparent'}; ${el.shape === 'circle' ? 'border-radius: 50%;' : ''} opacity: ${el.opacity || 1};"></div>`
      case "qr_placeholder":
        return `<div class="element" style="left: ${el.x}px; top: ${el.y}px; width: ${el.size || 72}px; height: ${el.size || 72}px; border: 2px dashed #999; display: flex; align-items: center; justify-content: center; background: #f5f5f5; font-size: 8pt; color: #666;">QR Code</div>`
      default:
        return ""
    }
  }

  saveAndContinue() {
    this.markAsSaved()
    this.design.orientation = this.orientation
    this.designDataTarget.value = JSON.stringify(this.design)

    if (this.hasOrientationInputTarget) {
      this.orientationInputTarget.value = this.orientation
    } else {
      const orientationInput = document.createElement("input")
      orientationInput.type = "hidden"
      orientationInput.name = "orientation"
      orientationInput.value = this.orientation
      this.formTarget.appendChild(orientationInput)
    }

    const selectedGuests = this.guestCheckboxTargets.filter(cb => cb.checked)
    selectedGuests.forEach(cb => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = "invitation_ids[]"
      input.value = cb.value
      this.formTarget.appendChild(input)
    })

    this.formTarget.submit()
  }
}
